/* ==========================================================================
   DOUXDS — the acquisition quiz. Avatar 2.0.

   Questions, copy and screen order come from the Claude Design handoff
   (design_handoff/ui_kits/quiz_funnel/) — the researched 9-screen funnel.
   The quiz-funnel-page engine is only the brain underneath: it renders these
   screens, scores them and hands off. Engine + quiz.css are never edited.

   Deltas from the design kit are flagged inline with WHY. Everything else is
   the design's own copy, verbatim.
   ========================================================================== */

window.QUIZ_CONFIG = {
  id: "douxds-method-quiz",
  brand: "DOUXDS",
  /* No logo file exists in the rebrand. The wordmark is TYPESET — display
     face, uppercase, wide-tracked — per DESIGN_SYSTEM.md. Never add an image
     mark here; quiz.theme.css styles the text brand + ™. */
  debug: false,

  quizUrl: "index.html",
  resultsUrl: "results.html",
  /* The product page, NOT a cart permalink. Damon's call 2026-08-13: the
     quiz hands off to the product page and lets the customer choose one-time
     or subscribe there, rather than dropping a subscription into the cart on
     their behalf. The Vitals subscription plan is still live on that page
     (20% off) if they want it.

     Note the plan is labelled "Deliver every month" but bills every 75 days —
     worth fixing in the subscription app regardless of where the quiz points. */
  offerUrl: "https://douxds.com/products/the-vitals-set",

  tracking: { eventPrefix: "douxds_quiz" },

  /* ---- 01 · Entry -------------------------------------------------------
     Design screen 01. The four badge cells are the arrow methods-grid — the
     highest-reach creative format in the campaign teardown (bad habit →
     ugly outcome, method → good outcome).

     The rating strip is BUSINESS-WIDE, confirmed by Damon 2026-08-13 — it is
     not the count on the Vitals Set product page, which shows only its own.

     It rides in `socialProof`, not `rating`: the engine only paints
     `intro.rating` inside the hero-image landing branch, and this is the
     centered intro. socialProof renders directly under the CTA, which is
     where the mockup puts the strip anyway. quiz-brand.js tints the stars.

     DELTA: the mockup's third item reads "Results in 90 days". That is an
     efficacy claim. Swapped to the brand's own named entity, THE DAY 90
     GUARANTEE — a refund promise is a fact we can stand behind, a results
     timeframe is not.                                                      */
  intro: {
    eyebrow: "No filters. Just the result.",
    /* Three lines, matching the design kit, which uses TWO Headline blocks:
         { top: "How to look", accent: "better." }  →  HOW TO LOOK / better.
         { top: "Starting today." }                 →  STARTING TODAY.
       Collapsing them into one made line two read as a lowercase
       continuation of a single sentence, which looked like a mistake.
       `.q-hl` is display:block, so the text after it falls to its own line
       and inherits the uppercase display styling from the h1. */
    title: "How to look [[better.]] Starting today.",
    /* ---- Proof carousel ------------------------------------------------
       Replaces the arrow tips-grid. Real, unretouched customer photos —
       every one visually reviewed before it went in. Composed as diptychs
       (before | after, split by a brass hairline) per DESIGN_SYSTEM.md,
       and served from the Shopify CDN.

       NOT from the Figma: those before/after faces are AI-generated
       Higgsfield renders with invented names. Never ship those as results.

       Source of the originals:
       Drive → douxds/Creative Assets/2. Content/1. Creators/Curated/
               Before and After/Raws/  (separate Before/After files) */
    proof: {
      caption: "Start fixing your base.",
      slides: [
        { name: "Joseph", image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-joseph.jpg",
          rating: 5, quote: "Cleared up acne made my face smooth and clean" },
        { name: "Rojan",  image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-rojan.jpg",
          rating: 5, quote: "Redness from my face went away and so did scarring and dark spots." },
        { name: "Kadin",  image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-kadin.jpg",
          rating: 4, quote: "Face always feels so good after washing in the shower, and the lotion is fantastic" },
        { name: "Mina",   image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-mina.jpg",
          rating: 5, quote: "I enjoy the smooth feeling after" }
      ]
    },

    ctaText: "Get started",
    socialProof: "★★★★★ 4.8 · 1,600+ reviews · Day 90 guarantee"
  },

  questions: [
    /* ---- 02 · Concerns · design screen 02 -----------------------------
       The router. Multi-select is right: these stack in real life, and the
       combination is what picks the emphasis on the results page.
       Scars maps to the marks profile (both are post-inflammatory), big
       pores maps to acne (both are congestion). Five answers, three
       methods — no orphan options.                                        */
    {
      id: "concerns",
      type: "multi",
      text: "Select your skin [[concerns.]]",
      hint: "Tap all that apply.",
      options: [
        { id: "acne",  icon: "acne",   label: "Acne",        scores: { acne: 3 },  factor: "Active breakouts",
          verdict: "Your face is keeping a receipt for every breakout." },
        { id: "bumps", icon: "razor",  label: "Razor bumps", scores: { bumps: 3 }, factor: "Razor bumps after shaving",
          verdict: "A new razor treats the shave. The bumps come from your skin." },
        { id: "marks", icon: "mark",   label: "Dark marks",  scores: { marks: 3 }, factor: "Dark marks that outlast the pimple",
          verdict: "The bumps might go away. The dark marks stay." },
        { id: "scars", icon: "scar",   label: "Scars",       scores: { marks: 2 }, factor: "Texture and scarring",
          verdict: "Clear skin reflects light evenly. Texture is what stops that." },
        { id: "pores", icon: "pores",  label: "Big pores",   scores: { acne: 2 },  factor: "Congested pores",
          verdict: "Big pores read as oil — and oil's the part you can actually change." }
      ]
    },

    /* ---- 03 · Duration · design screen 03 -----------------------------
       DELTA: the mockup hardcodes "How long have you had the dark marks?".
       Concerns is multi-select, so a concern-specific variant would either
       fire more than once or contradict what they picked. Generic phrasing,
       same two-line device, same intent — how entrenched is this.

       No `factor` on these: the engine caps computed.factors at 4, and this
       is the weakest signal of the four questions. Dropping it keeps a slot
       for the goal answer, which is the design's personalisation payoff.   */
    {
      id: "duration",
      text: "How long has this [[been going on?]]",
      options: [
        { id: "recent", icon: "clock",  label: "Just recently",             scores: { acne: 1 } },
        { id: "months", icon: "cal",    label: "A few months",              scores: { acne: 1, marks: 1 } },
        { id: "always", icon: "loop",   label: "As long as I can remember", scores: { marks: 2, bumps: 1 } }
      ]
    },

    /* ---- 04 · Diagnostic · design screen 04 ---------------------------
       The strongest question in the set. It maps the objection: a man on
       tret needs a different answer than a man on bar soap. Six options,
       design copy verbatim.

       Prescription deliberately carries NO score — we route those to an
       honest "don't drop your derm" line rather than scoring them into a
       method that competes with a prescription.                           */
    {
      id: "regimen",
      text: "What are you using [[right now?]]",
      options: [
        { id: "soap",   icon: "bar",     label: "Bar soap and that's it",                    scores: { acne: 2, bumps: 1 }, factor: "Bar soap only",
          verdict: "Strip your skin and it makes twice the oil." },
        { id: "basics", icon: "bottle",  label: "CeraVe / Cetaphil basics",                  scores: { acne: 1 },           factor: "Drugstore basics",
          verdict: "Gentle keeps you level. Nothing in it is working on acne or dark marks." },
        { id: "harsh",  icon: "burn",    label: "Salicylic acid or benzoyl peroxide",        scores: { acne: 2 },           factor: "Already tried the harsh stuff",
          verdict: "It works right up until it burns. Then you stop. That's the problem." },
        { id: "ord",    icon: "dropper", label: "The Ordinary (niacinamide, that stuff)",    scores: { marks: 1 },          factor: "Actives, no system",
          verdict: "You've got the actives. You haven't got the order to run them in." },
        { id: "script", icon: "rx",      label: "Prescription (tret / adapalene)",           factor: "On a prescription",
          verdict: "It replaces nothing your doctor gave you. It runs beside it." },
        { id: "dunno",  icon: "question",label: "I don't know what half these products are", scores: { acne: 1 },           factor: "Starting from scratch",
          verdict: "Nothing to unlearn. That's the easiest place to start." }
      ]
    },

    /* ---- 05 · The goal · design screen 05 -----------------------------
       Identity, not symptom. Carries no score on purpose — it does not
       change which method fits, it changes why the man is here. Captured
       as a factor so the results page can say it back to him.             */
    {
      id: "goal",
      text: "What's the [[goal?]]",
      options: [
        { id: "today", icon: "sunrise", label: "Look better starting today",
          desc: "Fastest visible change. You want the mirror different this month, not next year.",
          factor: "You want it moving now" },
        { id: "her",   icon: "spark",   label: "Become the version she never forgets",
          desc: "You have someone in mind. This is about how you show up in front of them.",
          factor: "You want to be unforgettable" },
        { id: "clean", icon: "clean",   label: "Build a clean aesthetic",
          desc: "A routine that just runs. Clear skin as part of being put together.",
          factor: "You want a clean aesthetic" },
        { id: "ten",   icon: "peak",    label: "Be a 10",
          desc: "Nothing left on the table. You want the whole base handled.",
          factor: "You are going all the way" }
      ]
    },

    /* ---- 06 · Proof · design screen 06 --------------------------------
       Sells nothing. It exists to reset attention before the ask.

       PLACEHOLDER: the design pairs this with a real before/after diptych
       (the mockup names "Zion D."). No verified asset was supplied, and a
       named customer result never ships unverified. The copy below makes no
       claim. Pull a real before/after + first name from Brotherhood
       Intelligence, then add the image.                                   */
    {
      id: "proof",
      type: "interstitial",
      eyebrow: "Building the Willow Method",
      title: "Skin reflects what you [[do daily.]]",
      body: "No shortcuts. Just consistency.",
      /* A SECOND set of customers, deliberately not the four on the intro:
         repeating the same faces halfway through reads as "these four are
         all we have". Same rules as the intro set — real photos, real
         verified reviews from the store's review export, nothing retouched. */
      proof: {
        caption: "Same method. New dudes.",
        slides: [
          { name: "Tevvon", image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-tevvon.jpg", rating: 5,
            quote: "Formulas eliminated my acne in a matter of 3 months. Best way to get rid of your acne if you want clearer skin." },
          { name: "Dillon", image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-dillon.jpg", rating: 5,
            quote: "Noticed a difference instantly! Completely cleared my acne on my face and neck." },
          { name: "Jay",    image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-jay.jpg", rating: 5,
            quote: "No more acne breakouts. Works great. Best product ever!" }
        ]
      },
      ctaText: "Keep going"
    },

    /* ---- 07 · Capture · design screen 07 ------------------------------
       DELTA: phone, not email — this avatar is young and SMS out-performs.
       The 📌 is kept: DESIGN_SYSTEM.md sanctions the pin on exactly this
       save/reminder moment and nowhere else.
       Never gates the result: Continue works with the field empty.        */
    {
      id: "phone",
      type: "email",
      text: "Save this before you [[forget. 📌]]",
      /* The method is NOT gated. This step only offers to put it on their
         phone, and the copy has to say so plainly — otherwise the number
         reads as the price of seeing the result, which is both untrue and
         the fastest way to lose the ones who won't hand over a number. */
      hint: "The Willow Method is ready either way. Want it on your phone? Drop your number and we'll text it over.",
      placeholder: "(555) 555-0134",
      ctaText: "Save my method",
      /* Rendered by quiz-brand.js as a real secondary button under the
         primary, not a text link — this is the honest way past the step now
         that the primary requires ten digits. */
      skipText: "No thanks, show my method"
    }
  ],

  /* ---- Lead capture → Postscript --------------------------------------
     WHY A RELAY. Postscript's Subscribers API
     (POST https://api.postscript.io/api/v2/subscribers) authenticates with a
     PRIVATE key, and their docs are explicit: "you should not expose your
     private API token to the outside world and should avoid calling the API
     from your frontend client." A Shopify page has no backend, so the key
     cannot live here. Their documented workaround is a relay (your own
     backend, or Zapier/Make holding the key privately).

     Their SDK is loaded on this store and does expose an undocumented
     `postscript.acquisition.optIn/verify` pair, but it is not in the public
     SDK reference and appears to require an OTP round-trip — an extra screen
     on a cold-traffic funnel, built on an API that can change without notice.
     Not worth it.

     The form itself stays exactly as designed; the relay is invisible.

     `endpoint` is the Pabbly Connect catch-hook (Catch Webhook, Preferred).
     The Pabbly workflow holds the Postscript key; this page never sees it.
     Blank it out and the funnel keeps working, silently. */
  lead: {
    endpoint: "https://connect.pabbly.com/webhook-listener/webhook/IjU3NjUwNTY1MDYzMzA0MzE1MjZkNTUzMyI_3D_pc/IjU3NjcwNTY4MDYzNjA0M2Q1MjZjNTUzNzUxMzIi_pc",
    source: "method-quiz",

    /* TCPA express written consent. Every element here is load-bearing:
       identity, "recurring automated marketing", the number provided, that
       consent is not a condition of purchase, frequency, rates, STOP/HELP,
       and hyperlinked Terms + Privacy. Postscript require the policy links.
       Do not trim this to make it prettier. */
    consentHtml:
      "By entering your number and tapping Save my method, you agree to receive " +
      "recurring automated marketing text messages from DOUXDS at the number " +
      "provided. Consent is not a condition of purchase. Msg frequency varies. " +
      "Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help. " +
      "<a href=\"https://douxds.com/policies/terms-of-service\" target=\"_blank\" rel=\"noopener\">Terms</a> · " +
      "<a href=\"https://douxds.com/policies/privacy-policy\" target=\"_blank\" rel=\"noopener\">Privacy Policy</a>"
  },

  /* ---- The analysing screen ------------------------------------------- */
  analyze: {
    title: "Building the Willow Method",
    steps: ["Reading your answers", "Matching the Willow Method", "Locking your 90"],
    durationMs: 2400
  },

  /* ---- 08 · Profiles ----------------------------------------------------
     One method, three entry points. Every man lands on the Willow Method
     because that is the catalogue — what changes is which bottle leads and
     why. That is the design's own results pattern: one method, personalised
     reasoning. Nothing here claims a result.                              */
  /* `learn` on each profile is now only a FALLBACK. The live "Why this one,
     now" copy is answer-driven — see results.matrix.why, keyed on what the
     visitor said they are currently using. */
  profiles: {
    acne: {
      name: "The Willow Method",
      headlineTop: "Stop feeding it",
      headlineAccent: "and it stops coming back.",
      learn: "Salicylic works. It also dries you out and burns, which is why most guys quit by week two. This one you can run every day without your face paying for it. Two minutes, morning and night.",
      lead: "Leads with WAR™ Face Wash",
      reco: {
        packageId: "vitals-set",
        includes: ["WAR™ Face Wash", "CRUSH™ Bump Eliminator", "PRIME™ Face Quench", "MIRO™ Face Towel"]
      }
    },
    marks: {
      name: "The Willow Method",
      headlineTop: "The acne's gone.",
      headlineAccent: "The dark marks aren't.",
      learn: "The spot clears in a week and the mark sits there for months — that is the part no free hack touches. You stop new marks at the source by keeping skin clear and calm every day. Two minutes, morning and night.",
      lead: "Leads with PRIME™ Face Quench",
      reco: {
        packageId: "vitals-set",
        includes: ["WAR™ Face Wash", "CRUSH™ Bump Eliminator", "PRIME™ Face Quench", "MIRO™ Face Towel"]
      }
    },
    bumps: {
      name: "The Willow Method",
      headlineTop: "Fresh lineup.",
      headlineAccent: "Wrecked neck.",
      learn: "New razors and bump serums treat the shave. Your skin decides whether the bumps come back, and harsh fixes keep it angry. CRUSH™ runs between lineups without the burn, so the neck holds. Two minutes, morning and night.",
      lead: "Leads with CRUSH™ Bump Eliminator",
      reco: {
        packageId: "vitals-set",
        includes: ["WAR™ Face Wash", "CRUSH™ Bump Eliminator", "PRIME™ Face Quench", "MIRO™ Face Towel"]
      }
    }
  },

  /* ---- 08 · Results page copy ------------------------------------------ */
  results: {
    eyebrow: "The Willow Method",
    /* Hero video at the top of the result — a customer actually running the
       routine. Muted + loop + playsinline so it autoplays on iOS without
       sound; the system allows exactly that ("nothing autoplays except muted
       looping product video"). */
    video: {
      src: "https://cdn.shopify.com/videos/c/vp/4852c8ea548a4e8aa108e8d60b972081/4852c8ea548a4e8aa108e8d60b972081.HD-720p-1.6Mbps-91587900.mp4",
      poster: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/preview_images/4852c8ea548a4e8aa108e8d60b972081.thumbnail.0000000000.jpg",
      alt: "A DOUXDS customer running the Willow Method."
    },
    image: "https://douxds.com/cdn/shop/files/TheVitalsSet_Updated_1x1-min.jpg",
    /* ==================================================================
       THE ANSWER MATRIX

       Every man lands on the same three products, so copy is the ONLY
       personalisation lever there is. This block is that lever, and it is
       deliberately split by which answer drives which line:

         headline  ← winning concern × how long they have had it
         sub       ← what they said the goal was
         why       ← what they are using RIGHT NOW  (the objection)

       Three independent axes, resolved by quiz-brand.js against the saved
       answers. 9 + 4 + 6 = 19 strings covering 3×3×4×6 = 216 answer paths,
       instead of writing 216 pages or — what it did before — showing all
       216 the same three sentences.

       Rules for anything added here:
       · Never claim a timeframe or an outcome. Describe the mechanism.
       · The `why` must argue against what they ARE doing. That is the whole
         point of asking the question.
       · The prescription branch never tells anyone to stop a prescription.
       ================================================================== */
    matrix: {
      /* ---- Headline: concern × duration --------------------------------
         Line one is the flat statement, line two is the brass turn. Read
         them as one sentence; that is the design system's headline device. */
      headline: {
        acne: {
          recent: { top: "Breakouts just started.",   accent: "Catch them now, before the marks." },
          months: { top: "Months of breakouts.",       accent: "They're not clearing on their own." },
          /* #83 house, verbatim across the two-line device. */
          always: { top: "Your face is keeping a receipt for every breakout.", accent: "Time to clear the tab." }
        },
        marks: {
          recent: { top: "The pimple's gone.",        accent: "The mark is just getting started." },
          months: { top: "The spot cleared in a week.", accent: "The mark is still here." },
          /* Bank #71, house, verbatim — the market's own version of this
             argument. My paraphrase said the same thing worse. */
          always: { top: "The bumps might go away.",  accent: "The dark marks stay." }
        },
        bumps: {
          recent: { top: "New razor. Same neck.",     accent: "That was never a shaving problem." },
          months: { top: "Every lineup costs you.",   accent: "Three days of bumps after." },
          always: { top: "Fresh lineup.",             accent: "Wrecked neck. Every single time." }
        }
      },

      /* ---- Subheadline: the goal they picked ---------------------------
         Says their own answer back to them, then what the method does about
         it. No timeframe — "this month" is a promise we cannot keep. */
      sub: {
        today: "You want this moving now, so it has to be something you'll actually keep doing. Two minutes, morning and night.",
        her:   "The best revenge is becoming unrecognizable. You can't change your bones. Your skin, you can.",
        clean: "You want something that just runs. Same three steps, same order, twice a day — nothing to decide.",
        ten:   "You're not leaving anything on the table. Start with your skin. It's the part of the whole thing you actually control."
      },
      /* Shown if the goal step was skipped or unreadable. */
      subDefault: "Three steps, same order, twice a day. Your skin's the part you actually control.",

      /* ---- Why this one, now: what they are using today ----------------
         The objection handler. Each one argues against their CURRENT
         routine, which is the only reason that question is worth asking. */
      why: {
        soap:   "Strip your skin and it makes twice the oil, and that oil is what's feeding the breakouts. Wash without stripping, put the moisture back, and your skin stops overreacting.",
        basics: "Gentle keeps you level. There's nothing in it working on acne or dark marks, so you're maintaining, not changing. This keeps the gentle part and adds the step that's missing.",
        harsh:  "Salicylic and benzoyl work right up until they dry you out and burn. That's why most guys quit by week two. This one you can run every day without your face paying for it.",
        ord:    "You've already got the actives. What you haven't got is an order to run them in. Wash, serum, moisturize — that's what turns a shelf of bottles into a routine.",
        script: "It replaces nothing your doctor gave you. It runs beside it. What a prescription usually costs you is dryness, and that's what a wash that doesn't strip and a real moisturizer are for.",
        dunno:  "Good — nothing to unlearn. Wash, serum, moisturize. You don't need to read an ingredient list to run it right."
      }
    },

    factorsTitle: "What you told us",
    whyTitle: "Why this one, now",

    /* ---- The diagnosis, straight after the reel ------------------------
       Damon, 2026-08-14: say back what they are doing and why it is not
       getting them there, before anything is sold. Rows are built by
       quiz-brand.js from the `verdict` on each option they actually picked
       — see the concerns and regimen questions above. Nothing generic can
       appear here; a row with no verdict is simply not rendered. */
    diagnoseTitle: "What you're doing now",
    diagnoseLead: "And why it isn't getting you there.",

    /* ---- The method, defined step by step -----------------------------
       Damon: say exactly what each product is, what it does, and how it is
       different from what they have already tried.

       Bank discipline, because this is the highest-claim block on the page:
       · Order and the three verbs are #88, verbatim: "Wash. Serum.
         Moisturize. Done."
       · WAR's `different` line is #74, verbatim — the willow-bark honesty
         ruling. The burned phrasing (#80 "natural salicylic acid") must
         never appear; skin cannot convert salicin and the forum knows.
       · PRIME says NOTHING about sun protection. The live product page
         implies SPF with no rating on record; that claim does not get
         repeated here.
       · Cosmetic verbs only — clears, calms, smooths, hydrates. CRUSH's
         own product NAME carries "Eliminator" (#73, kept permanently as
         enemy-naming); our sentences never borrow that verb. */
    stepsTitle: "What the Willow Method actually is",
    stepsLead: "Wash. Serum. Moisturize. Done.",
    steps: [
      { n: "01", verb: "Wash", name: "WAR™ Face Wash",
        does: "Washes off the day without stripping your skin.",
        different: "Salicylic acid burns, so you quit. This one doesn't, so you don't." },
      { n: "02", verb: "Serum", name: "CRUSH™ Bump Eliminator",
        does: "Goes on the bumps and the dark marks they leave.",
        different: "A new razor treats the shave. This treats the skin the bumps come from." },
      { n: "03", verb: "Moisturize", name: "PRIME™ Face Quench",
        does: "Puts back what the wash took.",
        different: "Skip this and your skin makes twice the oil. Most guys skip it." }
    ],
    stepsClose: "Three steps. Same order. Two minutes, morning and night.",

    /* ---- Proof, at the moment of the ask -------------------------------
       Damon: another before/after set with the reviews, making it clear
       these guys were where you are.

       These are the four from the intro, deliberately. The freshness law
       bans reusing result-faces across ASSETS; inside one funnel, showing
       them again at the ask is a callback, not slop — they last saw these
       six screens ago. The two pairs still unused (Zach, Bernard) are not
       held back for photo quality: they read ~40 and ~60, and this avatar
       is 15-24. "Guys just like you" has to survive a glance.

       Header is bank #68, whose source note says to adapt it to exactly
       this wording. */
    proof: {
      title: "Same method. New dudes.",
      /* #15, community arc-line, verbatim — the market's own words for a
         before/after. Emoji preserved; verbatim means verbatim. */
      lead: "2 different people 💀",
      slides: [
        { name: "Joseph", image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-joseph.jpg",
          rating: 5, quote: "Cleared up acne made my face smooth and clean" },
        { name: "Rojan",  image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-rojan.jpg",
          rating: 5, quote: "Redness from my face went away and so did scarring and dark spots." },
        { name: "Kadin",  image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-kadin.jpg",
          rating: 4, quote: "Face always feels so good after washing in the shower, and the lotion is fantastic" },
        { name: "Mina",   image: "https://cdn.shopify.com/s/files/1/0412/2804/2399/files/douxds-ba-mina.jpg",
          rating: 5, quote: "I enjoy the smooth feeling after" }
      ]
    },

    includesTitle: "What's in it",
    /* Rows in `includes` that get a brass FREE tag. It lives here rather than
       on the item because the engine flattens every include to a plain string
       (quiz-engine.js ~line 850), so an object would lose the flag. Matched on
       exact text by quiz-brand.js. */
    includesFree: ["MIRO™ Face Towel"],
    ctaText: "Start the method",
    /* NO downsell. The design kit puts a "just buy once" tertiary here, but
       that only earns its place when the primary is a subscription. With the
       primary going to the product page, both buttons would land in the same
       place — a second button that changes nothing is noise, not an offer. */
    guarantee: "Run it daily for 90 days. If you can't see the difference between day 1 and day 90, we refund you in full.",
    guaranteeTitle: "The Day 90 Guarantee",
    /* Damon, 2026-08-14: no subscribe/buy-once line. That choice lives on the
       product page; naming it here only adds a decision before the click. */
    reassure: ["Two minutes, morning and night", "All 3 + free MIRO™ towel"],
    /* Whisper-paren CTA microcopy — the lexicon's rule for quiz/page
       surfaces. Bank #29, gold, 669.3K product slide. */
    ctaWhisper: "(Start fixing your routine today.)"
  }
};
